// Typed API surface — thin wrappers over request(). Paths, methods and bodies match the
// REAL server contract (internal/httpapi/router.go). See docs/PORTAL_API_AUDIT.md.
//
// config.apiBase already ends with /api/v1, and http.ts:buildUrl() does apiBase + path,
// so every path here starts at /auth, /me, /profiles, /fields, /contacts, /subscribers,
// /share-links, /public — NOT re-prefixed with /api/v1.
import { request, logoutLocal } from './http.js';
import { config } from '../config.js';
import { uuid } from '../crypto/base64.js';
import type {
  Identity, FieldInstance, FieldInstanceCreate, Profile, Exposure, ShareLink, ShareLinkCreate,
  Subscription, AuthMethod, FieldType, Contact, Annotation, PublicView, PublicProfile, KeyMaterial,
  ExportJob, Collection, Tokens, Ceremony, Note, InviteView, PendingJoin, ApprovalRequest,
  Alias, AliasList, EmailState,
} from './types.js';

const idem = () => ({ idempotencyKey: uuid() });
const col = <T>(data: T[]): Collection<T> => ({ data, has_more: false });

// ---- server-wire response envelopes (shape as emitted by Go handlers) ----
interface FieldsEnvelope { fields: FieldInstance[]; }
interface ProfilesEnvelope { profiles: Profile[]; }
interface PasskeysEnvelope { passkeys: AuthMethod[]; }
interface TotpEnvelope { authenticators: AuthMethod[]; }
interface ShareLinksEnvelope { share_links: ShareLink[]; }
interface ContactsEnvelope { contacts: Contact[]; }
interface SubscribersEnvelope { subscribers: Subscription[]; }

// GET /version (service root) -> {version, commit, build_date}.
export interface ApiVersion { version: string; commit: string; build_date: string; }

// ---- adapters: server wire shape -> view-model shape ----
function adaptField(f: FieldInstance): FieldInstance {
  return { ...f, field_type_key: f.type ?? f.field_type_key };
}
export function adaptShareLink(l: ShareLink): ShareLink {
  const expires = l.expires_at ?? null;
  const revoked = !!l.revoked_at;
  const disabled = !revoked && !!l.disabled_at;
  const expired = !revoked && !disabled && expires != null && new Date(expires).getTime() < Date.now();
  const exhausted = !revoked && !disabled && l.max_uses != null && (l.use_count ?? 0) >= l.max_uses;
  const lifetime: ShareLink['lifetime_class'] = expires === null
    ? 'permanent'
    : (new Date(expires).getTime() - Date.now() > 90 * 864e5 ? 'long_lived' : 'temporary');
  const status: ShareLink['status'] = revoked ? 'revoked' : disabled ? 'disabled' : expired ? 'expired' : exhausted ? 'exhausted' : 'active';
  return {
    ...l,
    uses: l.use_count ?? 0,
    never_expires: expires === null,
    lifetime_class: lifetime,
    status,
    kind: l.kind ?? 'qr_link',
    // URL is now SERVER-PROVIDED (shareLinkResp.url = <origin>/id/<alias>/invite/<code>).
    // No client-side fallback to the dead /s/<token> scheme.
    url: l.url ?? undefined,
  };
}
// Prefer the server-provided subscriber name; fall back to username, then id.
function adaptSubscriberName(s: Subscription): Subscription['subscriber'] {
  const sub = s.subscriber;
  const displayName = sub?.display_name || sub?.username || s.subscriber_id || '';
  return { ...sub, display_name: displayName, username: sub?.username };
}

function adaptContact(c: Contact): Contact {
  const firstNote = (c.notes && c.notes[0]?.body) || undefined;
  const pending = c.pending_approval === true || c.sync_state === 'pending';
  // A pending-approval tentative contact is NEVER live (grayed until approved).
  const live = pending ? false
    : (c.live ?? (c.sync_state ? c.sync_state === 'live' || c.sync_state === 'synced' || c.sync_state === 'active' : c.status === 'active'));
  return {
    ...c,
    pending_approval: pending,
    subscription_id: c.subscription_id || c.id || '',
    display_name: c.display_name || c.owner_name || undefined,
    note: c.note ?? firstNote,
    live,
    fields: Array.isArray(c.fields) ? c.fields : [],
    labels: c.labels ?? [],
  };
}

export const api = {
  // ---- Auth & session ----
  auth: {
    usernameCheck: (username: string) =>
      request<{ available: boolean; reason?: string }>('/auth/username/check', { method: 'POST', auth: false, body: { username: username.trim() } }),

    // Passkey registration (Flow A): begin -> WebAuthn create -> finish.
    registerBegin: (username: string) => {
      const u = username.trim();
      return request<Ceremony>('/auth/register/begin', { method: 'POST', auth: false, body: { username: u, display_name: u } });
    },
    // finish carries no bearer token, but MUST send a DPoP proof: the server binds
    // the new session to the DPoP key presented here (dpop:true).
    registerFinish: (ceremony_id: string, credential: unknown, label?: string) =>
      request<Tokens>('/auth/register/finish', { method: 'POST', auth: false, dpop: true, body: { ceremony_id, credential, label }, ...idem() }),

    // Passkey login (Flow B): begin(handle) -> WebAuthn get -> finish.
    loginBegin: (handle: string) =>
      request<Ceremony>('/auth/login/begin', { method: 'POST', auth: false, body: { handle: handle.trim() } }),
    loginFinish: (ceremony_id: string, credential: unknown) =>
      request<Tokens>('/auth/login/finish', { method: 'POST', auth: false, dpop: true, body: { ceremony_id, credential } }),

    // Recovery-key login on a new device (unauth). Issues a session (login) when
    // the reusable recovery key (and TOTP code, if the account has one) verify.
    // dpop:true so the server binds the session to the DPoP key presented here.
    recover: (username: string, recovery_key: string, totp_code?: string) =>
      request<Tokens>('/auth/recover', {
        method: 'POST', auth: false, dpop: true,
        body: { username: username.trim(), recovery_key: recovery_key.trim(), ...(totp_code ? { totp_code: totp_code.trim() } : {}) },
      }),

    logout: () => request<void>('/auth/logout', { method: 'POST' }),

    // Passkeys management (server: /me/passkeys).
    listPasskeys: async (): Promise<Collection<AuthMethod>> => {
      const res = await request<PasskeysEnvelope>('/me/passkeys');
      return col((res.passkeys || []).map((p) => ({ ...p, kind: 'passkey' as const })));
    },
    deletePasskey: (id: string) => request<void>(`/me/passkeys/${id}`, { method: 'DELETE' }),

    // Add an additional passkey to the CURRENT authenticated account.
    // Uses the bound session (bearer token) — no DPoP needed, unlike signup.
    // Returns existing_passkeys so a post-login enroll step can gate on ===0
    // (mirrors joinRegisterBegin). Also used by the recovery-key post-login
    // "create a passkey on THIS device" step (the recovered user is authed).
    addPasskeyBegin: () => request<Ceremony & { existing_passkeys: number }>('/me/passkeys/begin', { method: 'POST', body: {} }),
    // ceremony_id-explicit finish used by the post-login enroll helper. The
    // server (handleAddPasskeyFinish) requires ceremony_id; this passes it
    // through alongside the credential + optional label.
    addPasskeyFinishCeremony: (ceremony_id: string, credential: unknown, label?: string) =>
      request<{ id: string; label: string; created_at: string }>('/me/passkeys/finish', { method: 'POST', body: { ceremony_id, credential, label } }),
    renamePasskey: (id: string, label: string) =>
      request<{ id: string; label: string }>(`/me/passkeys/${id}`, { method: 'PATCH', body: { label } }),

    // Aliases consumed by the Settings view (kept additive so both call styles work).
    passkeys: async (): Promise<Collection<AuthMethod>> => {
      const res = await request<PasskeysEnvelope>('/me/passkeys');
      return col((res.passkeys || []).map((p) => ({ ...p, kind: 'passkey' as const })));
    },
    removePasskey: (id: string) => request<void>(`/me/passkeys/${id}`, { method: 'DELETE' }),
    totpList: () => request<TotpEnvelope>('/me/totp'),

    // Combined auth-methods view (passkeys + confirmed TOTP) for the Settings screen.
    methods: async (): Promise<Collection<AuthMethod>> => {
      const [pk, totp] = await Promise.all([
        request<PasskeysEnvelope>('/me/passkeys').catch(() => ({ passkeys: [] as AuthMethod[] })),
        request<TotpEnvelope>('/me/totp').catch(() => ({ authenticators: [] as AuthMethod[] })),
      ]);
      const passkeys = (pk.passkeys || []).map((p) => ({ ...p, kind: 'passkey' as const }));
      const auths = (totp.authenticators || []).map((t) => ({ ...t, kind: 'totp' as const }));
      return col([...passkeys, ...auths]);
    },

    // TOTP (server: /me/totp/begin + /me/totp/finish). begin returns {id, secret, otpauth_url}.
    totpBegin: (label?: string) =>
      request<{ id: string; secret: string; otpauth_url: string }>('/me/totp/begin', { method: 'POST', body: { label }, ...idem() }),
    totpFinish: (id: string, code: string) =>
      request<{ id: string; confirmed: boolean }>('/me/totp/finish', { method: 'POST', body: { id, code } }),
    listTotp: async (): Promise<Collection<AuthMethod>> => {
      const res = await request<TotpEnvelope>('/me/totp');
      return col((res.authenticators || []).map((t) => ({ ...t, kind: 'totp' as const })));
    },
    deleteTotp: (id: string) => request<void>(`/me/totp/${id}`, { method: 'DELETE' }),
    renameTotp: (id: string, label: string) =>
      request<{ id: string; label: string }>(`/me/totp/${id}`, { method: 'PATCH', body: { label } }),

    recoveryKey: () =>
      request<{ recovery_key: string; note?: string }>('/me/recovery-key', { method: 'POST', ...idem() }),

    // ---- Primary email + verification (server: /me/email group) ----
    // Every method resolves to the same EmailState object (except remove -> 204).
    // Errors surface as ApiError (EMAIL_INVALID, EMAIL_TAKEN, CODE_INVALID,
    // CODE_EXPIRED, CODE_LOCKED, RESEND_TOO_SOON, ...) — the caller maps codes
    // to friendly copy and toasts them.
    email: {
      get: () => request<EmailState>('/me/email'),
      add: (email: string) =>
        request<EmailState>('/me/email', { method: 'POST', body: { email: email.trim() }, ...idem() }),
      verify: (code: string) =>
        request<EmailState>('/me/email/verify', { method: 'POST', body: { code: code.trim() } }),
      resend: () => request<EmailState>('/me/email/resend', { method: 'POST', ...idem() }),
      remove: () => request<void>('/me/email', { method: 'DELETE' }),
    },

    // First-run onboarding: capture first/last name right after signup. Populates
    // the user's given_name + family_name field values (kept exposed on the public
    // profile) so new users don't start with empty names.
    onboardingSetName: (given_name: string, family_name: string) =>
      request<{ given_name: string; family_name: string }>('/me/onboarding/name', { method: 'POST', body: { given_name, family_name } }),

    // Cross-device join (Flow C).
    joinIssue: () => request<{ join_id: string; code: string; expires_in: number }>('/auth/join/issue', { method: 'POST', ...idem() }),
    // Rolling device-join code (TOTP-style, 30s/6-digit) for the trusted device.
    joinCode: () => request<{ code: string; seconds_remaining: number; period: number }>('/auth/join/code'),
    joinRedeem: (username: string, code: string) =>
      request<{ join_id: string; status: string; message?: string }>('/auth/join/redeem', { method: 'POST', auth: false, body: { username, code } }),
    // Start a WebAuthn create ceremony bound to the EXISTING account behind an
    // APPROVED join (new device isn't authed yet). MUST be used instead of
    // registerBegin() for the join 'persist device' path — registerBegin treats
    // the handle as a new signup and 409s the user's own username as taken.
    joinRegisterBegin: (join_id: string) =>
      request<Ceremony & { existing_passkeys: number }>('/auth/join/register/begin', { method: 'POST', auth: false, body: { join_id } }),
    joinApprove: (join_id: string, method?: string) =>
      request<{ join_id: string; status: string }>('/auth/join/approve', { method: 'POST', body: { join_id, method } }),
    joinDeny: (join_id: string) =>
      request<{ join_id: string; status: string }>('/auth/join/deny', { method: 'POST', body: { join_id } }),
    // Trusted-device inbox: list this user's actionable awaiting-approval requests.
    joinPending: () =>
      request<{ requests: PendingJoin[] }>('/auth/join/pending'),
    joinFinish: (join_id: string, persist_device: boolean, ceremony_id?: string, credential?: unknown) =>
      request<Tokens>('/auth/join/finish', { method: 'POST', auth: false, dpop: true, body: { join_id, persist_device, ceremony_id, credential } }),
    // Unauth join-status poll for the WAITING (new) device: minimal non-PII
    // status so it can auto-complete on approval / bail on denied/expired.
    joinStatus: (join_id: string) =>
      request<{ status: string; seconds_remaining: number }>('/auth/join/status?join_id=' + encodeURIComponent(join_id), { auth: false }),
  },

  // ---- Identity ----
  me: () => request<Identity>('/me'),
  // PATCH /me updates mutable profile fields (currently display_name).
  updateMe: (patch: Partial<Identity>): Promise<Identity> =>
    request<Identity>('/me', { method: 'PATCH', body: {
      ...(patch.display_name !== undefined ? { display_name: patch.display_name } : {}),
      ...(patch.auto_accept_contacts !== undefined ? { auto_accept_contacts: patch.auto_accept_contacts } : {}),
    } }),
  // Accept a pending reciprocal share-back contact (the My Contacts "Add" button).
  acceptContact: (subId: string) =>
    request<{ status: string; subscription_id: string }>(`/contacts/${subId}/accept`, { method: 'POST' }),

  // ---- Username aliases (up to 5 total: 1 immutable primary + 4 aliases) ----
  aliases: () => request<AliasList>('/me/aliases'),
  addAlias: (alias: string) =>
    request<Alias>('/me/aliases', { method: 'POST', body: { alias: alias.trim() }, ...idem() }),
  removeAlias: (id: string) => request<void>(`/me/aliases/${id}`, { method: 'DELETE' }),
  checkAlias: (alias: string) =>
    request<{ available: boolean; reason?: string }>('/me/aliases/check', { method: 'POST', body: { alias: alias.trim() } }),

  // ---- Fields (server: top-level /fields) ----
  fields: async (): Promise<Collection<FieldInstance>> => {
    const res = await request<FieldsEnvelope>('/fields');
    return col((res.fields || []).map(adaptField));
  },
  addField: async (f: FieldInstanceCreate): Promise<FieldInstance> => {
    const body = { type: f.type ?? f.field_type_key, label: f.label, annotation: f.annotation, value: f.value };
    return adaptField(await request<FieldInstance>('/fields', { method: 'POST', body, ...idem() }));
  },
  updateField: async (id: string, patch: Partial<FieldInstance>, ifMatch?: string): Promise<FieldInstance> => {
    // True partial PATCH: only send keys the caller actually provided, so a
    // value-only edit never blanks the qualifier `label` and vice-versa.
    const body: Record<string, string> = {};
    if (patch.type !== undefined || patch.field_type_key !== undefined) body.type = (patch.type ?? patch.field_type_key) as string;
    if (patch.label !== undefined) body.label = patch.label;
    if (patch.annotation !== undefined) body.annotation = patch.annotation;
    if (patch.value !== undefined) body.value = patch.value;
    return adaptField(await request<FieldInstance>(`/fields/${id}`, { method: 'PATCH', body, ifMatch }));
  },
  deleteField: (id: string) => request<void>(`/fields/${id}`, { method: 'DELETE' }),
  // Well-known field-type catalog: GET /field-types -> {field_types:[...]}.
  fieldTypes: async (): Promise<Collection<FieldType>> => {
    const res = await request<{ field_types: FieldType[] }>('/field-types');
    return col(res.field_types || []);
  },

  // ---- Profiles & exposure ----
  profiles: async (): Promise<Collection<Profile>> => {
    const res = await request<ProfilesEnvelope>('/profiles');
    return col(res.profiles || []);
  },
  profile: (id: string) => request<Profile>(`/profiles/${id}`),
  createProfile: (p: Partial<Profile>) =>
    request<Profile>('/profiles', { method: 'POST', body: { name: p.name, description: p.description, icon: p.icon, color: p.color, is_public: p.is_public }, ...idem() }),
  updateProfile: (id: string, patch: Partial<Profile>, ifMatch?: string) =>
    request<Profile>(`/profiles/${id}`, { method: 'PATCH', body: { name: patch.name, description: patch.description, icon: patch.icon, color: patch.color, is_public: patch.is_public }, ifMatch }),
  // Enable/disable a profile (public profile can be disabled but not deleted).
  setProfileEnabled: (id: string, enabled: boolean) =>
    request<Profile>(`/profiles/${id}`, { method: 'PATCH', body: { enabled } }),
  deleteProfile: (id: string) => request<void>(`/profiles/${id}`, { method: 'DELETE' }),
  setProfileAvatar: (id: string, body: { asset_id?: string; url?: string }) =>
    request<{ status: string }>(`/profiles/${id}/avatar`, { method: 'PUT', body }),
  deleteProfileAvatar: (id: string) => request<void>(`/profiles/${id}/avatar`, { method: 'DELETE' }),

  // GET /profiles/{id}/exposure — returns currently-exposed field entries so the
  // edit matrix can re-populate its toggles after save/navigation.
  exposures: (id: string): Promise<Collection<Exposure>> =>
    request<{ exposures: Exposure[] }>(`/profiles/${id}/exposure`)
      .then((r) => col<Exposure>(r.exposures || []))
      .catch(() => col<Exposure>([])),
  // PUT /profiles/{id}/exposure with {entries:[{field_id,exposed}]}.
  setExposures: (id: string, exposures: Exposure[], stepUp?: string) =>
    request<{ status: string }>(`/profiles/${id}/exposure`, { method: 'PUT', body: { entries: exposures }, stepUp }),
  // No server-rendered preview route; the caller degrades to "unavailable".
  preview: (_id: string): Promise<{ fields: { key: string; label: string; value: string }[] }> =>
    Promise.reject(new Error('preview unavailable')),
  keyMaterial: (id: string): Promise<KeyMaterial> => Promise.reject(new Error(`key-material unavailable for ${id}`)),

  // ---- Subscriptions — owner side ----
  // Server exposes GET /subscribers (no pending workflow). Map into the Subscription view model.
  subscriptions: async (_q: { profile_id?: string; status?: string } = {}): Promise<Collection<Subscription>> => {
    const res = await request<SubscribersEnvelope>('/subscribers');
    let subs = (res.subscribers || []).map((s): Subscription => ({
      ...s, status: s.status as Subscription['status'],
      subscriber: adaptSubscriberName(s), since: s.added_at, labels: s.labels ?? [],
    }));
    if (_q.profile_id) subs = subs.filter((s) => s.profile_id === _q.profile_id);
    if (_q.status) subs = subs.filter((s) => s.status === _q.status);
    return col(subs);
  },
  // Owner approve/deny workflow does not exist server-side yet (audit §4) — client no-ops.
  approveSub: (id: string): Promise<Subscription> => Promise.reject(new Error(`Approvals aren't supported yet (${id}).`)),
  denySub: (id: string): Promise<Subscription> => Promise.reject(new Error(`Deny isn't supported yet (${id}).`)),
  moveSub: (id: string, profile_id: string): Promise<void> =>
    request<void>(`/subscribers/${id}`, { method: 'PATCH', body: { profile_id } }),
  // MOVE an existing subscription to a different profile of the same owner.
  moveSubscriberProfile: (id: string, profileId: string): Promise<void> =>
    request<void>(`/subscribers/${id}`, { method: 'PATCH', body: { profile_id: profileId } }),
  // ADD an existing subscriber to ANOTHER of my profiles (2nd subscription).
  addSubscriberToProfile: (subscriberId: string, profileId: string): Promise<Subscription> =>
    request<Subscription>('/subscribers', { method: 'POST', body: { subscriber_id: subscriberId, profile_id: profileId }, ...idem() }),
  revokeSub: (id: string) => request<void>(`/subscribers/${id}`, { method: 'DELETE' }),

  // View-facing aliases (used by dashboard/subscribers/profiles views).
  subscribers: async (): Promise<Collection<Subscription>> => {
    const res = await request<SubscribersEnvelope>('/subscribers');
    const subs = (res.subscribers || []).map((s): Subscription => ({
      ...s, status: s.status as Subscription['status'],
      subscriber: adaptSubscriberName(s), since: s.added_at, labels: s.labels ?? [],
    }));
    return col(subs);
  },
  revokeSubscriber: (id: string) => request<void>(`/subscribers/${id}`, { method: 'DELETE' }),

  // ---- Explicit subscription approvals (owner side) ----
  // GET /approvals -> pending requests (newest first) + count.
  approvals: (): Promise<{ requests: ApprovalRequest[]; count: number }> =>
    request<{ requests: ApprovalRequest[]; count: number }>('/approvals'),
  // GET /approvals/count -> lightweight badge/banner poll.
  approvalsCount: async (): Promise<number> =>
    (await request<{ count: number }>('/approvals/count').catch(() => ({ count: 0 }))).count,
  approveRequest: (id: string) =>
    request<{ status: string; subscription_id: string }>(`/approvals/${id}/approve`, { method: 'POST', ...idem() }),
  denyRequest: (id: string) =>
    request<{ status: string }>(`/approvals/${id}/deny`, { method: 'POST', ...idem() }),
  setSubscriberPaused: (id: string, paused: boolean) =>
    request<{ status: string; paused: boolean }>(`/subscribers/${id}/pause`, { method: 'POST', body: { paused } }),

  // Top-level auth-method aliases consumed by the Settings view.
  passkeys: (): Promise<Collection<AuthMethod>> => api.auth.passkeys(),
  totpList: () => request<TotpEnvelope>('/me/totp'),
  removePasskey: (id: string) => request<void>(`/me/passkeys/${id}`, { method: 'DELETE' }),

  // ---- My contacts — subscriber side ----
  contacts: async (_q: { label?: string; q?: string } = {}): Promise<Collection<Contact>> => {
    const res = await request<ContactsEnvelope>('/contacts');
    return col((res.contacts || []).map(adaptContact));
  },
  contact: async (subId: string): Promise<Contact> => adaptContact(await request<Contact>(`/contacts/${subId}`)),
  // Cheap authed poll: has an upstream field this subscriber can see changed?
  // rev changes on change; active_peers hints the fanout size for client jitter.
  contactsRefreshSignal: () =>
    request<{ rev: string | number; active_peers: number }>('/contacts/refresh-signal'),
  // Re-pull the owner-profile's currently-exposed fields and reconcile the snapshot.
  refreshContact: async (subId: string): Promise<Contact> =>
    adaptContact(await request<Contact>(`/contacts/${subId}/refresh`, { method: 'POST' })),
  // Drop a non-syncing (stale) field from the synced contact card.
  removeContactField: (subId: string, fieldKey: string) =>
    request<void>(`/contacts/${subId}/fields/${encodeURIComponent(fieldKey)}`, { method: 'DELETE' }),
  // No /pull route; keep the signature so views compile, surface a friendly error.
  pullContact: (subId: string): Promise<Contact> => Promise.reject(new Error(`Manual sync isn't available yet (${subId}).`)),
  unsubscribe: (subId: string) => request<void>(`/contacts/${subId}`, { method: 'DELETE' }),

  // Annotation = notes + labels. Add/replace the first note, then set labels.
  upsertAnnotation: async (subId: string, a: Annotation): Promise<Annotation> => {
    if (a.note !== undefined) {
      const existing = await request<Contact>(`/contacts/${subId}`).catch(() => null);
      const firstNote = existing?.notes?.[0];
      if (firstNote?.id) {
        await request<Note>(`/contacts/${subId}/notes/${firstNote.id}`, { method: 'PATCH', body: { body: a.note } });
      } else {
        await request<Note>(`/contacts/${subId}/notes`, { method: 'POST', body: { body: a.note } });
      }
    }
    if (a.labels !== undefined) {
      await request<{ labels: string[] }>(`/contacts/${subId}/labels`, { method: 'PUT', body: { labels: a.labels } });
    }
    return { subject_subscription_id: subId, note: a.note, labels: a.labels };
  },
  setLabels: (subId: string, labels: string[]) =>
    request<{ labels: string[] }>(`/contacts/${subId}/labels`, { method: 'PUT', body: { labels } }),
  // Direct note CRUD (add / update / remove) mapping 1:1 to the annotation endpoints.
  addNote: (subId: string, body: string) =>
    request<Note>(`/contacts/${subId}/notes`, { method: 'POST', body: { body } }),
  updateNote: (subId: string, noteId: string, body: string) =>
    request<Note>(`/contacts/${subId}/notes/${noteId}`, { method: 'PATCH', body: { body } }),
  deleteNote: (subId: string, noteId: string) =>
    request<void>(`/contacts/${subId}/notes/${noteId}`, { method: 'DELETE' }),
  // Labels are derived client-side from the contact set (no /labels route).
  labels: (): Promise<Collection<string>> => Promise.resolve(col<string>([])),

  // ---- Sharing ----
  shareLinks: async (): Promise<Collection<ShareLink>> => {
    const res = await request<ShareLinksEnvelope>('/share-links');
    return col((res.share_links || []).map(adaptShareLink));
  },
  createShareLink: async (l: ShareLinkCreate, stepUp?: string): Promise<ShareLink> => {
    let expires_in_sec: number | undefined;
    if (l.expires_at) {
      const secs = Math.round((new Date(l.expires_at).getTime() - Date.now()) / 1000);
      if (secs > 0) expires_in_sec = secs;
    }
    const body = { profile_id: l.profile_id, label: l.label, max_uses: l.max_uses ?? undefined, expires_in_sec, approval_mode: l.approval_mode ?? 'auto' };
    return adaptShareLink(await request<ShareLink>('/share-links', { method: 'POST', body, stepUp, ...idem() }));
  },
  revokeShareLink: (id: string) => request<void>(`/share-links/${id}`, { method: 'DELETE' }),

  // POST /share-links/{id}/disabled: temporarily toggle redeemability WITHOUT
  // removing the link or changing its expiry. Reversible; returns the fresh link.
  setShareLinkDisabled: async (id: string, disabled: boolean): Promise<ShareLink> =>
    adaptShareLink(await request<ShareLink>(`/share-links/${id}/disabled`, { method: 'POST', body: { disabled } })),

  // PATCH /share-links/{id}: edit label and/or expiry. Pass expires_at (ISO) to
  // set a new expiry, or permanent:true to make it never expire. Omitting both
  // leaves expiry untouched. Mirrors createShareLink's expires_at -> relative
  // seconds conversion the server expects.
  updateShareLink: async (
    id: string,
    patch: { label?: string; expires_at?: string | null; permanent?: boolean; max_uses?: number; approval_mode?: 'auto' | 'explicit' },
    stepUp?: string,
  ): Promise<ShareLink> => {
    const body: Record<string, unknown> = {};
    if (patch.label !== undefined) body.label = patch.label;
    if (patch.max_uses !== undefined) body.max_uses = patch.max_uses; // 0 => unlimited
    if (patch.approval_mode !== undefined) body.approval_mode = patch.approval_mode;
    if (patch.permanent) {
      body.permanent = true;
    } else if (patch.expires_at) {
      const secs = Math.round((new Date(patch.expires_at).getTime() - Date.now()) / 1000);
      if (secs > 0) body.expires_in_sec = secs;
    }
    return adaptShareLink(await request<ShareLink>(`/share-links/${id}`, { method: 'PATCH', body, stepUp }));
  },

  // POST /share-links/{id}/reactivate: revive a revoked/expired link. The caller
  // MUST choose a new expiry: pass expires_at (ISO) or permanent:true. label and
  // max_uses may also be updated. Returns the fresh (now-active) link.
  reactivateShareLink: async (
    id: string,
    opts: { expires_at?: string | null; permanent?: boolean; label?: string; max_uses?: number },
    stepUp?: string,
  ): Promise<ShareLink> => {
    const body: Record<string, unknown> = {};
    if (opts.label !== undefined) body.label = opts.label;
    if (opts.max_uses !== undefined) body.max_uses = opts.max_uses; // 0 => unlimited
    if (opts.permanent) {
      body.permanent = true;
    } else if (opts.expires_at) {
      const secs = Math.round((new Date(opts.expires_at).getTime() - Date.now()) / 1000);
      if (secs > 0) body.expires_in_sec = secs;
    }
    return adaptShareLink(await request<ShareLink>(`/share-links/${id}/reactivate`, { method: 'POST', body, stepUp }));
  },

  // DELETE /share-links/{id}/permanent: HARD delete a single link (distinct from
  // the soft revoke DELETE /share-links/{id}).
  deleteShareLinkPermanent: (id: string) =>
    request<void>(`/share-links/${id}/permanent`, { method: 'DELETE' }),

  // DELETE /share-links/revoked/all: bulk HARD delete all revoked + naturally-
  // expired links (active and merely-disabled links are untouched).
  purgeRevokedShareLinks: () =>
    request<{ deleted: number }>(`/share-links/revoked/all`, { method: 'DELETE' }),

  // Redeeming a share happens via POST /subscribe. The server accepts EITHER a legacy
  // opaque {token} OR an owner-scoped {alias, code} (invite-code flow). See
  // internal/httpapi/contacts.go:handleSubscribe.
  subscribe: async (token: string): Promise<Subscription> => {
    if (!token) throw new Error('No share token supplied.');
    const c = await request<Contact>('/subscribe', { method: 'POST', body: { token }, ...idem() });
    return { id: c.id ?? c.subscription_id ?? '', status: (c.status as Subscription['status']) ?? 'active', profile_id: c.profile_id };
  },
  // Owner-scoped invite-code redemption: {alias, code} -> subscription. For an
  // EXPLICIT-approval link the server responds 202 {status:'pending_approval'};
  // we surface that as a Subscription with status 'pending' so callers can toast
  // "awaiting approval" instead of "subscribed".
  subscribeByCode: async (alias: string, code: string, shareBackProfileId?: string): Promise<Subscription> => {
    if (!alias || !code) throw new Error('An invite alias and code are required.');
    const body: Record<string, unknown> = { alias, code };
    if (shareBackProfileId) body.share_back_profile_id = shareBackProfileId;
    const c = await request<Contact & { status?: string }>('/subscribe', { method: 'POST', body, ...idem() });
    if (c.status === 'pending_approval') return { id: '', status: 'pending', profile_id: c.profile_id };
    return { id: c.id ?? c.subscription_id ?? '', status: (c.status as Subscription['status']) ?? 'active', profile_id: c.profile_id };
  },
  // Read-only resolve of a coded invite (no secrets) backing /id/<alias>/invite/<code>.
  resolveInvite: (alias: string, code: string) =>
    request<InviteView>(`/public/id/${encodeURIComponent(alias)}/invite/${encodeURIComponent(code)}`, { auth: false }),
  redeemShare: async (payload: { token?: string; code?: string; alias?: string; subscriber_public_key?: JsonWebKey }): Promise<Subscription> => {
    if (payload.alias && payload.code) return api.subscribeByCode(payload.alias, payload.code);
    const token = payload.token ?? payload.code;
    if (!token) throw new Error('No share token supplied.');
    const c = await request<Contact>('/subscribe', { method: 'POST', body: { token }, ...idem() });
    return { id: c.id ?? c.subscription_id ?? '', status: (c.status as Subscription['status']) ?? 'active', profile_id: c.profile_id };
  },

  // ---- Public profile & export ----
  // No /me/public-profile route; derive enabled/alias from the user's handle.
  getPublicProfile: async (): Promise<PublicProfile> => {
    const me = await request<Identity>('/me').catch(() => null);
    if (me?.handle) return { enabled: true, alias: me.handle };
    return { enabled: false };
  },
  setPublicProfile: (_alias: string, _profile_id: string): Promise<PublicProfile> =>
    Promise.reject(new Error('Managing the public profile is not available yet.')),
  disablePublicProfile: (): Promise<void> => Promise.reject(new Error('Disabling the public profile is not available yet.')),
  publicView: (alias: string) => request<PublicView>(`/public/${alias}`, { auth: false }),
  // Owner-only: subscriber-facing preview of ONE of my profiles (any profile),
  // returning the same shape as publicView so the same card template renders.
  previewProfile: (id: string) => request<PublicView>(`/profiles/${id}/preview`),
  exportVcardUrl: (alias: string) => `/public/${alias}/export?format=vcard`,

  // ---- Data lifecycle ----
  startExport: (): Promise<ExportJob> => Promise.reject(new Error('Data export is not available yet.')),
  exportStatus: (jobId: string): Promise<ExportJob> => Promise.reject(new Error(`Export status unavailable (${jobId}).`)),
  // Permanently delete the caller's own account. Requires the user to type their
  // exact username as confirmation (server re-checks it against the session's
  // account). On success the server has erased every record; it echoes the
  // deleted passkey credential IDs (base64url) so the client can best-effort
  // hint the authenticator to forget them. Local session/state is cleared here.
  deleteAccount: async (confirmUsername: string): Promise<{ credential_ids: string[] }> => {
    const res = await request<{ deleted?: boolean; credential_ids?: string[] }>('/me', {
      method: 'DELETE',
      body: { confirm_username: confirmUsername },
    });
    // Clear local session + any remembered-account hints defensively (the
    // account-picker's `vid.accounts` module may not be on this branch yet).
    try {
      logoutLocal();
      localStorage.removeItem('vid.accounts');
    } catch { /* non-fatal */ }
    return { credential_ids: res?.credential_ids ?? [] };
  },

  // Backend build metadata from the ROOT /version endpoint (NOT under /api/v1).
  // Best-effort: used only for the sidebar version footer, so it degrades quietly.
  version: async (): Promise<ApiVersion> => {
    // apiBase ends with /api/v1; /version lives at the service root.
    const rootBase = config.apiBase.replace(/\/$/, '').replace(/\/api\/v1$/, '');
    const res = await fetch(rootBase + '/version', { headers: { accept: 'application/json' } });
    if (!res.ok) throw new Error('version ' + res.status);
    return res.json() as Promise<ApiVersion>;
  },
};

export type Api = typeof api;

// Endpoints the server does not implement yet (see docs/PORTAL_API_AUDIT.md §4). Grouped
// separately so views can degrade gracefully and the "coming soon" surface is obvious.
export const unsupported = {
  getPublicProfile: api.getPublicProfile,
  setPublicProfile: api.setPublicProfile,
  disablePublicProfile: api.disablePublicProfile,
  startExport: api.startExport,
  exportStatus: api.exportStatus,
  updateMe: api.updateMe,
  preview: api.preview,
  keyMaterial: api.keyMaterial,
};
